import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMembership } from '../contexts/MembershipContext';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { Shield } from 'lucide-react';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const colorPalette = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#d946ef', '#ec4899', '#f43f5e',
];

const generateColor = (str) => {
  if (!str) return colorPalette[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorPalette[Math.abs(hash) % colorPalette.length];
};


export default function ProfileButton() {
  const { currentUser, signOutUser } = useAuth();
  const { membership, loading: membershipLoading } = useMembership();
  const { isAdmin } = useAdminStatus();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/login');
  };

  const handleMembershipClick = () => {
    setIsOpen(false);
    navigate('/membership?from=profile');
  };

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleAdminClick = () => {
    setIsOpen(false);
    navigate('/operations-control-center');
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="profile-button-unique flex items-center justify-center w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-transform duration-150 hover:scale-105 overflow-hidden border-0 shadow-none p-0"
        style={{
          backgroundColor: currentUser.photoURL ? 'transparent' : generateColor(currentUser.displayName || currentUser.email),
        }}
      >
        {currentUser.photoURL ? (
          <img
            src={currentUser.photoURL}
            alt="User"
            crossOrigin="anonymous"
            className="w-full h-full object-cover rounded-full block"
          />
        ) : (
          <span className="text-white text-lg font-bold select-none flex items-center justify-center w-full h-full leading-none">
            {currentUser.displayName?.charAt(0).toUpperCase() || currentUser.email?.charAt(0).toUpperCase()}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Dropdown Arrow */}
          <div className="absolute right-4 top-[52px] z-20">
            <svg width="24" height="12" viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12L0 0H24L12 12Z" fill="#fff" stroke="#e5e7eb"/>
            </svg>
          </div>
          <div className="absolute right-0 mt-8 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 py-1 ring-1 ring-black ring-opacity-5 z-10">
            <div className="px-6 py-3 border-b border-gray-200">
              <p className="text-base font-semibold text-gray-800 truncate">{currentUser.displayName || 'User'}</p>
              <p className="text-sm text-gray-600 truncate">{currentUser.email}</p>
            </div>
            <button
              onClick={handleMembershipClick}
              className="w-full text-left px-6 py-3 border-b border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Membership</p>
              {membershipLoading ? (
                <p className="text-sm text-gray-700">Loading...</p>
              ) : membership ? (
                <div>
                  <p className="text-sm text-gray-700 font-semibold capitalize">
                    {membership.type === 'team' && membership.teamId ? 'Team' : membership.tier}
                    {membership.status === "expired" && (
                      <span className="ml-1 text-xs text-red-600 font-normal">(Expired)</span>
                    )}
                    {membership.canceledAt && membership.currentPeriodEnd && (
                      <span className="ml-1 text-xs text-orange-600 font-normal">
                        (Cancels at period end • {formatDate(membership.currentPeriodEnd)})
                      </span>
                    )}
                  </p>
                  {membership.startDate && (
                    <p className="text-xs text-gray-500">
                      {membership.canceledAt ? (
                        // Canceled subscription - show since date only
                        <>Since {formatDate(membership.startDate)}</>
                      ) : membership.status === 'active' && !membership.endDate && !membership.canceledAt ? (
                        // Active subscription without end date (auto-renewing)
                        <>Since {formatDate(membership.startDate)} • Auto-renewing</>
                      ) : membership.status === 'active' && membership.endDate ? (
                        // Active but will end (trial or has end date)
                        <>Renews: {formatDate(membership.endDate)}</>
                      ) : membership.endDate ? (
                        // Has end date (expired or other status)
                        <>{formatDate(membership.startDate)} - {formatDate(membership.endDate)}</>
                      ) : (
                        // Fallback for other cases
                        <>Since {formatDate(membership.startDate)}</>
                      )}
                      {membership.status === "expired" && membership.expired_reason && (
                        <span className="block text-red-500 mt-1">
                          {membership.expired_reason}
                        </span>
                      )}
                    </p>
                  )}
                  {membership.type === 'team' && membership.teamId && (
                    <p className="text-xs text-gray-500 mt-1">
                      Via team membership
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-700">Not enrolled</p>
              )}
            </button>
            <button
              onClick={handleSettingsClick}
              className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200"
            >
              Settings
            </button>
            {isAdmin && (
              <>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={handleAdminClick}
                  className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-200 flex items-center"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Dashboard
                </button>
              </>
            )}
            <button
              onClick={handleSignOut}
              className="w-full text-left px-6 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-b-xl"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
} 